<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.badges', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.name')" for="name" />
                            <FormInput
                                required
                                id="name"
                                v-model="form.name"
                                name="name"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.image')" for="image" />
                            <input
                                id="image"
                                @input="form.image = $event.target.files[0]"
                                name="image"
                                type="file"
                                accept="image/*"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.image" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.description')" for="description" />
                            <FormTextarea
                                id="description"
                                v-model="form.description"
                                name="description"
                                type="text"
                                max="480"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.description" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_automatic')" for="is_automatic" />
                            <input
                                id="is_automatic"
                                type="checkbox"
                                v-model="form.is_automatic"
                                name="is_automatic"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_automatic" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_hidden_before_earning')"
                                for="is_hidden_before_earning"
                            />
                            <input
                                id="is_hidden_before_earning"
                                type="checkbox"
                                v-model="form.is_hidden_before_earning"
                                name="is_hidden_before_earning"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_hidden_before_earning" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.published_at')" for="published_at" />
                            <FormInput
                                required
                                id="published_at"
                                v-model="form.published_at"
                                name="published_at"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.published_at" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>
            <TitleFieldsFormSection v-show="form.is_automatic">
                <template v-slot:title>
                    {{ $tc('messages.triggers', 1) }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.triggers', 1)" for="trigger" />
                            <FormSelect
                                :required="form.is_automatic"
                                id="trigger"
                                v-model="form.trigger"
                                :options="options.triggers"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.trigger" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6" v-show="form.trigger">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.threshold')" for="threshold" />
                            <FormInput
                                :required="form.trigger"
                                id="threshold"
                                v-model="form.threshold"
                                name="threshold"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.threshold" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>
            <TitleFieldsFormSection v-show="!form.is_automatic">
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_requestable')" for="is_requestable" />
                            <input
                                id="is_requestable"
                                type="checkbox"
                                v-model="form.is_requestable"
                                name="is_requestable"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_requestable" class="mt-2" />
                        </div>
                    </div>
                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel
                                :value="$t('messages.is_automatically_accepted')"
                                for="is_automatically_accepted"
                            />
                            <input
                                id="is_automatically_accepted"
                                type="checkbox"
                                v-model="form.is_automatically_accepted"
                                name="is_automatically_accepted"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_automatically_accepted" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>
            <TitleFieldsFormSection v-show="!form.is_automatic">
                <template v-slot:title>
                    <div>
                        {{ $tc('messages.groups', 2) }}
                    </div>
                    <div class="text-sm text-gray-500">
                        {{ $t('messages.select_limit_to_groups_explanation') }}
                    </div>
                </template>
                <div>
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        <input
                            name="all_groups"
                            type="checkbox"
                            v-model="form.all_groups"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        {{ $t('messages.all') }}
                    </label>
                </div>
                <div class="md:grid md:grid-cols-12 md:gap-6" v-show="!form.all_groups">
                    <div class="sm:col-span-6">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.limit_to_selected_groups') }}
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            <label class="ml-1 mr-3" v-for="group in options.groups" :key="group.value">
                                <input
                                    name="group_ids[]"
                                    type="checkbox"
                                    :value="group.value"
                                    v-model="form.group_ids"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                    {{ group.text }}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'options', 'gamificationSet', 'badge'],
    data() {
        return {
            form: this.$inertia.form({
                name: this.badge.name,
                description: this.badge.description,
                is_automatic: this.badge.is_automatic,
                is_hidden_before_earning: this.badge.is_hidden_before_earning,
                trigger: this.badge.trigger,
                threshold: this.badge.threshold,
                published_at: this.badge.published_at,
                is_requestable: this.badge.is_requestable,
                is_automatically_accepted: this.badge.is_automatically_accepted,
                group_ids: this.badge.group_ids,
                image: null,
                all_groups: true,
            }),
        };
    },
    watch: {
        'form.is_automatic': {
            handler(newValue) {
                if (newValue === false) {
                    this.form.trigger = null;
                    this.form.threshold = null;
                }
            },
            deep: true,
        },
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
    },
};
</script>
